// Phase C — User Profile + Privacy + Notifications + Security.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { DashboardLayout } from "@/pages/Dashboard";
import { useLanguage, useAuth, API } from "@/App";
import { cvApi } from "@/lib/cv";
import { ensurePushRegistered, disablePush, sendTestPush, getCurrentSubscriptionEndpoint } from "@/lib/push";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import BackBar from "@/components/BackBar";
import {
  User, ShieldCheck, Bell, KeyRound, FileText, Loader2,
  Smartphone, LogOut, CheckCircle2, XCircle, Crown,
  Scale, ChevronLeft, ChevronRight,
} from "lucide-react";

const SECTIONS = [
  { id: "profile",       ar: "البروفايل",          en: "Profile",        Icon: User },
  { id: "privacy",       ar: "الخصوصية",           en: "Privacy",        Icon: ShieldCheck },
  { id: "notifications", ar: "الإشعارات",          en: "Notifications",  Icon: Bell },
  { id: "security",      ar: "الأمان",              en: "Security",       Icon: KeyRound },
  { id: "files",         ar: "ملفاتي",              en: "My Files",       Icon: FileText },
  { id: "legal",         ar: "الوثائق القانونية",  en: "Legal",          Icon: Scale },
];

const Toggle = ({ value, onChange, testid }) => (
  <button onClick={() => onChange(!value)} data-testid={testid}
    className={`w-11 h-6 rounded-full transition relative ${value ? "bg-emerald-500" : "bg-slate-700"}`}>
    <span className={`absolute top-0.5 ${value ? "right-0.5" : "left-0.5"} w-5 h-5 bg-white rounded-full transition`} />
  </button>
);

const auth = (t) => ({ headers: { Authorization: `Bearer ${t}` } });

export default function Profile() {
  const { t, language } = useLanguage();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("profile");
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [resumes, setResumes] = useState([]);
  const [twofa, setTwofa] = useState({ open: false, secret: "", uri: "", code: "" });
  const [pwd, setPwd] = useState({ old: "", new1: "", new2: "" });

  useEffect(() => {
    if (!token) { navigate("/login"); return; }
    let mounted = true;
    (async () => {
      try {
        const me = await axios.get(`${API}/me/profile`, auth(token)).then((r) => r.data);
        if (mounted) setData(me);
        const r = await cvApi.list(token);
        if (mounted) setResumes(r || []);
      } catch { /* silent */ }
    })();
    return () => { mounted = false; };
  }, [token, navigate]);

  if (!data) {
    return <DashboardLayout title={t("ملفي", "My Profile")}><div className="flex items-center justify-center p-20"><Loader2 className="animate-spin text-yellow-500" /></div></DashboardLayout>;
  }

  const saveProfile = async (patch) => {
    setSaving(true);
    try { const next = await axios.put(`${API}/me/profile`, patch, auth(token)).then((r) => r.data); setData(next); toast.success(t("تم الحفظ", "Saved")); }
    catch (e) { toast.error(e?.response?.data?.detail || t("فشل الحفظ", "Save failed")); }
    finally { setSaving(false); }
  };

  const savePrivacy = async (patch) => {
    const next = await axios.put(`${API}/me/privacy`, patch, auth(token)).then((r) => r.data);
    setData((d) => ({ ...d, privacy: next }));
  };

  const saveNotif = async (patch) => {
    const next = await axios.put(`${API}/me/notifications`, patch, auth(token)).then((r) => r.data);
    setData((d) => ({ ...d, notifications: next }));
  };

  const changePassword = async () => {
    if (pwd.new1 !== pwd.new2) { toast.error(t("كلمتا المرور غير متطابقتين", "New passwords don't match")); return; }
    if (pwd.new1.length < 8)   { toast.error(t("8 أحرف على الأقل", "At least 8 characters")); return; }
    try {
      await axios.put(`${API}/me/password`, { old_password: pwd.old, new_password: pwd.new1 }, auth(token));
      toast.success(t("تم تغيير كلمة المرور", "Password changed"));
      setPwd({ old: "", new1: "", new2: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || t("فشل", "Failed")); }
  };

  const start2FA = async () => {
    try {
      const r = await axios.post(`${API}/me/2fa/setup`, {}, auth(token)).then((x) => x.data);
      setTwofa({ open: true, secret: r.secret, uri: r.otpauth_uri, code: "" });
    } catch { toast.error("Failed to start 2FA setup"); }
  };

  const enable2FA = async () => {
    try {
      await axios.post(`${API}/me/2fa/enable`, { secret: twofa.secret, code: twofa.code }, auth(token));
      toast.success(t("تم تفعيل المصادقة الثنائية", "Two-factor enabled"));
      setData((d) => ({ ...d, totp_enabled: true }));
      setTwofa({ open: false, secret: "", uri: "", code: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || t("رمز خاطئ", "Invalid code")); }
  };

  const disable2FA = async () => {
    const code = window.prompt(t("أدخل رمز التحقق الحالي:", "Enter your current 2FA code:"));
    if (!code) return;
    try {
      await axios.post(`${API}/me/2fa/disable`, { code }, auth(token));
      setData((d) => ({ ...d, totp_enabled: false }));
      toast.success(t("تم إيقاف المصادقة الثنائية", "Two-factor disabled"));
    } catch (e) { toast.error(e?.response?.data?.detail || t("فشل", "Failed")); }
  };

  const revokeAll = async () => {
    if (!window.confirm(t("سيتم تسجيل خروجك من جميع الأجهزة. متابعة؟", "You will be logged out from all devices. Continue?"))) return;
    try { await axios.post(`${API}/me/sessions/revoke-all`, {}, auth(token)); toast.success(t("تم تسجيل الخروج من كل الأجهزة", "Logged out from all devices")); navigate("/login"); } catch { /* silent */ }
  };

  const inputCls = "bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white w-full text-sm focus:border-yellow-500 outline-none";

  return (
    <DashboardLayout title={t("ملفي وإعداداتي", "My Profile & Settings")}>
      <BackBar to="/dashboard" label_ar="الرئيسية" label_en="Home"
        crumbs={[{ to: "/dashboard", ar: "الرئيسية", en: "Home" }, { ar: "ملفي وإعداداتي", en: "Profile & Settings" }]} />
      <div className="grid lg:grid-cols-[220px_1fr] gap-4" data-testid="profile-page">
        {/* Sidebar */}
        <nav className="glass-card rounded-2xl p-2 flex lg:flex-col gap-1 overflow-x-auto" data-testid="profile-sidebar">
          {SECTIONS.map((s) => (
            <button key={s.id} onClick={() => setTab(s.id)}
              className={`text-sm px-3 py-2 rounded-xl inline-flex items-center gap-2 whitespace-nowrap transition ${tab === s.id ? "bg-yellow-500 text-black font-semibold" : "text-slate-300 hover:bg-slate-800"}`}
              data-testid={`profile-tab-${s.id}`}>
              <s.Icon size={14} />{language === "ar" ? s.ar : s.en}
            </button>
          ))}
        </nav>

        {/* Main panel */}
        <div className="glass-card rounded-2xl p-5 space-y-5">
          {/* PROFILE */}
          {tab === "profile" && (
            <div className="space-y-3" data-testid="section-profile">
              <h2 className="text-lg font-bold text-white">{t("بياناتي الشخصية", "My information")}</h2>
              <div className="flex items-center gap-3">
                {data.avatar
                  ? <img src={data.avatar.startsWith("http") ? data.avatar : `${API}/uploads/${data.avatar}`} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-yellow-500/40" data-testid="profile-avatar-img" />
                  : <div className="w-16 h-16 rounded-full bg-gradient-to-br from-yellow-500 to-amber-700 flex items-center justify-center text-white font-bold text-2xl">{(data.name || "?").slice(0, 1).toUpperCase()}</div>}
                <div className="flex-1">
                  <div className="text-white font-semibold">{data.name}</div>
                  <div className="text-xs text-slate-400">{data.email}</div>
                  <div className="flex gap-2 mt-2">
                    <label className="cursor-pointer text-xs bg-slate-900 border border-slate-700 text-slate-300 rounded-lg px-3 py-1.5 hover:border-yellow-500/40 inline-flex items-center gap-1" data-testid="avatar-upload">
                      📷 {t("غيّر الصورة", "Change picture")}
                      <input type="file" accept="image/png,image/jpeg,image/jpg,image/webp" className="hidden"
                        onChange={async (e) => {
                          const f = e.target.files?.[0]; if (!f) return;
                          if (f.size > 5 * 1024 * 1024) { toast.error(t("الصورة كبيرة جداً (الحد 5MB)", "Image too large (max 5MB)")); return; }
                          const fd = new FormData(); fd.append("file", f);
                          try {
                            const r = await axios.post(`${API}/me/avatar`, fd, { headers: { ...auth(token).headers, "Content-Type": "multipart/form-data" } });
                            setData((d) => ({ ...d, avatar: r.data.avatar }));
                            toast.success(t("تم تحديث الصورة", "Avatar updated"));
                          } catch { toast.error(t("فشل رفع الصورة", "Upload failed")); }
                        }} />
                    </label>
                    {data.avatar && (
                      <button onClick={async () => { await axios.delete(`${API}/me/avatar`, auth(token)); setData((d) => ({ ...d, avatar: "" })); toast.success(t("تمت الإزالة", "Removed")); }}
                        className="text-xs text-red-400 hover:text-red-300 px-3 py-1.5" data-testid="avatar-remove">{t("إزالة", "Remove")}</button>
                    )}
                  </div>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><label className="text-xs text-slate-400">{t("الاسم الكامل", "Full name")}</label><Input value={data.name} onChange={(e) => setData({...data, name: e.target.value})} className={inputCls} data-testid="profile-name" /></div>
                <div><label className="text-xs text-slate-400">{t("اسم المستخدم", "Username")}</label><Input value={data.username} onChange={(e) => setData({...data, username: e.target.value.replace(/[^a-z0-9_.]/gi, "").toLowerCase()})} placeholder="omar_soudy" className={inputCls} data-testid="profile-username" /></div>
                <div><label className="text-xs text-slate-400">{t("المدينة", "City")}</label><Input value={data.city} onChange={(e) => setData({...data, city: e.target.value})} placeholder="Houston" className={inputCls} data-testid="profile-city" /></div>
                <div><label className="text-xs text-slate-400">{t("الولاية", "State")}</label><Input value={data.state} onChange={(e) => setData({...data, state: e.target.value})} placeholder="TX" className={inputCls} data-testid="profile-state" /></div>
              </div>
              <div><label className="text-xs text-slate-400">{t("نبذة قصيرة", "Short bio")}</label><textarea value={data.bio} onChange={(e) => setData({...data, bio: e.target.value})} rows={3} maxLength={240} placeholder={t("اكتب نبذة قصيرة عنك…", "Tell us a bit about yourself…")} className={inputCls + " resize-none"} data-testid="profile-bio" /></div>
              <Button onClick={() => saveProfile({ name: data.name, username: data.username, city: data.city, state: data.state, bio: data.bio })} disabled={saving} className="btn-primary" data-testid="profile-save-btn">
                {saving ? <Loader2 className="animate-spin ml-2" size={14} /> : <CheckCircle2 size={14} className="ml-2" />}{t("حفظ التغييرات", "Save changes")}
              </Button>

              {/* Membership card */}
              <MembershipCard token={token} t={t} navigate={navigate} />
            </div>
          )}

          {/* PRIVACY */}
          {tab === "privacy" && (
            <div className="space-y-3" data-testid="section-privacy">
              <h2 className="text-lg font-bold text-white">{t("الخصوصية", "Privacy")}</h2>
              {[
                ["show_online", t("إظهار «متصل الآن»", "Show ‘Online Now’")],
                ["show_last_seen", t("إظهار «آخر ظهور»", "Show ‘Last Seen’")],
                ["show_read_receipts", t("إظهار علامة القراءة ✓✓", "Show read receipts ✓✓")],
                ["allow_chat_adds", t("السماح للآخرين بإضافتي إلى محادثاتهم", "Allow others to start chats with me")],
              ].map(([key, label]) => (
                <div key={key} className="flex items-center justify-between py-2 border-b border-slate-800">
                  <span className="text-sm text-slate-200">{label}</span>
                  <Toggle value={!!data.privacy[key]} onChange={(v) => savePrivacy({ [key]: v })} testid={`privacy-${key}`} />
                </div>
              ))}
              <div className="py-2">
                <label className="text-sm text-slate-200 mb-1 block">{t("من يستطيع مراسلتي؟", "Who can message me?")}</label>
                <select value={data.privacy.who_can_message} onChange={(e) => savePrivacy({ who_can_message: e.target.value })} className={inputCls} data-testid="privacy-who-can-message">
                  <option value="everyone">{t("الجميع", "Everyone")}</option>
                  <option value="friends">{t("الأصدقاء فقط", "Friends only")}</option>
                  <option value="nobody">{t("لا أحد", "Nobody")}</option>
                </select>
              </div>
            </div>
          )}

          {/* NOTIFICATIONS */}
          {tab === "notifications" && (
            <div className="space-y-4" data-testid="section-notifications">
              <h2 className="text-lg font-bold text-white">{t("الإشعارات", "Notifications")}</h2>

              {/* New: Push notifications block — toggles backed by /api/notifications/preferences */}
              <PushNotificationsBlock token={token} t={t} />

              <h3 className="text-sm font-semibold text-yellow-300 pt-2">{t("إعدادات قديمة", "Legacy settings")}</h3>
              {[
                ["messages", t("إشعارات الرسائل", "Message notifications")],
                ["jobs", t("إشعارات الوظائف", "Job notifications")],
                ["housing", t("إشعارات السكن", "Housing notifications")],
                ["email", t("إشعارات البريد الإلكتروني", "Email notifications")],
              ].map(([key, label]) => (
                <div key={key} className="flex items-center justify-between py-2 border-b border-slate-800">
                  <span className="text-sm text-slate-200">{label}</span>
                  <Toggle value={!!data.notifications[key]} onChange={(v) => saveNotif({ [key]: v })} testid={`notif-${key}`} />
                </div>
              ))}
            </div>
          )}

          {/* SECURITY */}
          {tab === "security" && (
            <div className="space-y-5" data-testid="section-security">
              <h2 className="text-lg font-bold text-white">{t("الأمان", "Security")}</h2>

              {/* Change password */}
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-yellow-300">{t("تغيير كلمة المرور", "Change password")}</h3>
                <Input type="password" placeholder={t("كلمة المرور الحالية", "Current password")} value={pwd.old}  onChange={(e) => setPwd({...pwd, old:  e.target.value})} className={inputCls} data-testid="pwd-old" />
                <Input type="password" placeholder={t("كلمة المرور الجديدة", "New password")}     value={pwd.new1} onChange={(e) => setPwd({...pwd, new1: e.target.value})} className={inputCls} data-testid="pwd-new1" />
                <Input type="password" placeholder={t("تأكيد الجديدة", "Confirm new password")}   value={pwd.new2} onChange={(e) => setPwd({...pwd, new2: e.target.value})} className={inputCls} data-testid="pwd-new2" />
                <Button onClick={changePassword} className="btn-primary" data-testid="pwd-save-btn"><KeyRound size={14} className="ml-2" />{t("تغيير", "Change password")}</Button>
              </div>

              {/* 2FA */}
              <div className="space-y-2 border-t border-slate-800 pt-4">
                <h3 className="text-sm font-semibold text-yellow-300 flex items-center gap-2"><Smartphone size={14} />{t("المصادقة الثنائية (TOTP)", "Two-Factor Auth (TOTP)")}</h3>
                {data.totp_enabled ? (
                  <div className="flex items-center justify-between bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3">
                    <div className="text-sm text-emerald-200 inline-flex items-center gap-2"><CheckCircle2 size={14} />{t("مفعّلة", "Enabled")}</div>
                    <Button onClick={disable2FA} variant="outline" className="border-red-500/40 text-red-300" data-testid="2fa-disable-btn"><XCircle size={14} className="ml-1" />{t("إيقاف", "Disable")}</Button>
                  </div>
                ) : twofa.open ? (
                  <div className="space-y-2 bg-slate-900/60 rounded-xl p-3 border border-slate-700" data-testid="2fa-enable-form">
                    <p className="text-xs text-slate-300">{t("افتح تطبيق Authenticator وأدخل المفتاح يدوياً:", "Open your Authenticator app and enter this secret:")}</p>
                    <code className="block bg-slate-950 border border-slate-700 rounded p-2 text-xs text-yellow-300 select-all break-all">{twofa.secret}</code>
                    <p className="text-[10px] text-slate-500 break-all">{twofa.uri}</p>
                    <Input value={twofa.code} onChange={(e) => setTwofa({...twofa, code: e.target.value.replace(/\D/g, "").slice(0, 6)})} placeholder="123456" className={inputCls + " text-center text-lg tracking-widest font-mono"} data-testid="2fa-code-input" />
                    <Button onClick={enable2FA} disabled={twofa.code.length !== 6} className="btn-primary w-full" data-testid="2fa-verify-btn"><CheckCircle2 size={14} className="ml-2" />{t("تفعيل", "Enable")}</Button>
                  </div>
                ) : (
                  <Button onClick={start2FA} variant="outline" className="border-yellow-500/40 text-yellow-300" data-testid="2fa-start-btn"><Smartphone size={14} className="ml-2" />{t("ابدأ إعداد المصادقة الثنائية", "Set up two-factor")}</Button>
                )}
              </div>

              {/* Sessions */}
              <div className="space-y-2 border-t border-slate-800 pt-4">
                <h3 className="text-sm font-semibold text-yellow-300">{t("الجلسات النشطة", "Active sessions")}</h3>
                <p className="text-xs text-slate-400">{t("اضغط الزر لتسجيل الخروج من جميع الأجهزة الأخرى مباشرة.", "Click below to immediately sign out of every other device.")}</p>
                <Button onClick={revokeAll} variant="outline" className="border-red-500/40 text-red-300" data-testid="revoke-sessions-btn"><LogOut size={14} className="ml-2" />{t("تسجيل الخروج من كل الأجهزة", "Log out from all devices")}</Button>
              </div>
            </div>
          )}

          {/* FILES */}
          {tab === "files" && (
            <div className="space-y-3" data-testid="section-files">
              <h2 className="text-lg font-bold text-white">{t("ملفاتي", "My files")}</h2>
              <p className="text-xs text-slate-400">{t("جميع السير الذاتية وخطابات التغطية التي أنشأتها.", "All resumes and cover letters you have created.")}</p>
              {resumes.length === 0 ? (
                <div className="text-center text-slate-400 py-8 text-sm">{t("لا توجد ملفات بعد.", "No files yet.")}
                  <div className="mt-2"><Button onClick={() => navigate("/cv")} className="btn-primary"><FileText size={14} className="ml-2" />{t("اذهب لمركز السيرة الذاتية", "Go to Resume Center")}</Button></div>
                </div>
              ) : resumes.map((r) => (
                <div key={r.resume_id} className="flex items-center justify-between bg-slate-900/60 rounded-xl p-3 border border-slate-800" data-testid={`file-${r.resume_id}`}>
                  <div className="flex items-center gap-2"><FileText size={14} className="text-yellow-500" /><span className="text-sm text-white">{r.title || t("بدون عنوان", "Untitled")}</span><Badge className="bg-slate-800 text-slate-300 text-[10px]">{r.template}</Badge></div>
                  <Button onClick={() => navigate(`/cv/edit/${r.resume_id}`)} size="sm" className="btn-primary">{t("فتح", "Open")}</Button>
                </div>
              ))}
              <div className="border-t border-slate-800 pt-3 mt-3">
                <h3 className="text-sm font-semibold text-yellow-300 inline-flex items-center gap-2"><Crown size={14} />{t("الباقة الحالية", "Current plan")}</h3>
                <p className="text-xs text-slate-400 mt-1">{t("مجاني — يمكنك ترقية باقتك من صفحة Career Success Package.", "Free — upgrade from the Career Success Package page.")}</p>
                <Button onClick={() => navigate("/cv/premium")} className="btn-primary mt-2"><Crown size={14} className="ml-2" />{t("ترقية الباقة", "Upgrade")}</Button>
              </div>
            </div>
          )}

          {/* LEGAL — Terms, Privacy, Refund, Disclaimer (open in new tab) */}
          {tab === "legal" && (
            <div className="space-y-3" data-testid="section-legal">
              <h2 className="text-lg font-bold text-white">{t("الوثائق القانونية", "Legal Documents")}</h2>
              <p className="text-xs text-slate-400">
                {t(
                  "اطّلع على الشروط والسياسات المُحدَّثة لـ Arab Sanad. متوفّرة بالعربية والإنجليزية وتُحدَّث بانتظام لتعكس نظام العمولات، عضوية مقدّمي الخدمات، ومركز السيرة الذاتية.",
                  "Review Arab Sanad's updated terms and policies. Available in Arabic and English and updated regularly to reflect the commission model, provider memberships, and Resume Center."
                )}
              </p>
              <div className="grid sm:grid-cols-2 gap-3 pt-2">
                {[
                  { to: "/terms",      ar: "شروط الاستخدام",        en: "Terms of Service",  desc_ar: "قواعد استخدام المنصة وعلاقتك مع Arab Sanad.", desc_en: "Platform usage rules and your relationship with Arab Sanad.", Icon: FileText,      testid: "legal-terms" },
                  { to: "/privacy",    ar: "سياسة الخصوصية",        en: "Privacy Policy",    desc_ar: "كيف نجمع بياناتك ونستخدمها ونحميها.",          desc_en: "How we collect, use, and protect your data.",            Icon: ShieldCheck,   testid: "legal-privacy" },
                  { to: "/refunds",    ar: "سياسة الاسترداد والإلغاء", en: "Refund Policy",   desc_ar: "شروط استرداد العضوية، السيرة الذاتية، وحجوزات المطار.", desc_en: "Refund terms for memberships, Resume Center, and airport bookings.", Icon: KeyRound,    testid: "legal-refunds" },
                  { to: "/disclaimer", ar: "إخلاء المسؤولية",       en: "Disclaimer",        desc_ar: "حدود مسؤولية Arab Sanad ودور المنصة الوسيطة.",  desc_en: "Arab Sanad's liability boundaries and our role as a marketplace.", Icon: Scale, testid: "legal-disclaimer" },
                ].map((doc) => (
                  <a
                    key={doc.to}
                    href={doc.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-start gap-3 bg-slate-900/60 hover:bg-slate-900 border border-slate-800 hover:border-yellow-500/40 rounded-xl p-4 transition-colors"
                    data-testid={doc.testid}
                  >
                    <div className="w-10 h-10 rounded-lg bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center shrink-0 group-hover:bg-yellow-500/20">
                      <doc.Icon size={18} className="text-yellow-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1 text-white font-semibold text-sm">
                        {language === "ar" ? doc.ar : doc.en}
                        {language === "ar"
                          ? <ChevronLeft  size={14} className="text-slate-500 group-hover:text-yellow-400 transition-colors" />
                          : <ChevronRight size={14} className="text-slate-500 group-hover:text-yellow-400 transition-colors" />}
                      </div>
                      <p className="text-[11px] text-slate-400 mt-1 leading-snug">
                        {language === "ar" ? doc.desc_ar : doc.desc_en}
                      </p>
                    </div>
                  </a>
                ))}
              </div>
              <p className="text-[11px] text-slate-500 pt-3 border-t border-slate-800 mt-3">
                {t(
                  "بمواصلة استخدامك Arab Sanad، فإنك توافق على هذه الوثائق. آخر تحديث: فبراير 2026.",
                  "By continuing to use Arab Sanad, you agree to these documents. Last updated: February 2026."
                )}
              </p>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

// ─── MembershipCard — shows current plan + billing history + cancel ───
function MembershipCard({ token, t, navigate }) {
  const [m, setM] = useState(null);
  useEffect(() => {
    axios.get(`${API}/me/membership`, auth(token)).then((r) => setM(r.data)).catch(() => setM({ plan: "free", is_premium: false, billing: [] }));
  }, [token]);
  if (!m) return null;
  const cancel = async () => {
    if (!window.confirm(t("هل أنت متأكد من إلغاء الاشتراك؟", "Cancel your subscription?"))) return;
    try { await axios.post(`${API}/me/subscription/cancel`, {}, auth(token)); toast.success(t("تم الإلغاء — صالح حتى نهاية المدة", "Canceled — active until period end")); setM({ ...m, subscription_status: "canceled" }); }
    catch { toast.error(t("فشل الإلغاء", "Cancel failed")); }
  };
  return (
    <div className="border-t border-slate-800 pt-4 mt-5" data-testid="membership-card">
      <h3 className="text-sm font-semibold text-yellow-300 inline-flex items-center gap-2"><Crown size={14} />{t("العضوية والاشتراك", "Membership & Subscription")}</h3>
      <div className="flex items-center justify-between bg-slate-900/60 rounded-xl p-3 mt-2 border border-slate-800">
        <div>
          <div className="text-white font-semibold capitalize">{m.plan}{m.is_premium ? " ✨" : ""}</div>
          <div className="text-[11px] text-slate-400">{t("الحالة:", "Status:")} <span className="text-slate-200">{m.subscription_status}</span>{m.expires_at ? ` · ${t("تنتهي في", "Expires")} ${new Date(m.expires_at).toLocaleDateString()}` : ""}</div>
        </div>
        <div className="flex gap-2">
          {/* Only premium users see the Manage button. The public Subscriptions
              page no longer carries any consumer plans, so the "Upgrade" CTA
              is hidden for regular users to avoid dead-ends. */}
          {m.is_premium && (
            <Button onClick={() => navigate("/subscriptions")} variant="outline" className="border-yellow-500/40 text-yellow-300" data-testid="membership-upgrade-btn"><Crown size={12} className="ml-1" />{t("إدارة", "Manage")}</Button>
          )}
          {m.is_premium && m.subscription_status === "active" && (
            <Button onClick={cancel} variant="outline" className="border-red-500/40 text-red-300" data-testid="membership-cancel-btn">{t("إلغاء", "Cancel")}</Button>
          )}
        </div>
      </div>
      {(m.billing && m.billing.length > 0) && (
        <div className="mt-3" data-testid="billing-history">
          <div className="text-[11px] text-slate-500 mb-1">{t("سجل الفواتير", "Billing history")}</div>
          {m.billing.slice(0, 5).map((b, i) => (
            <div key={i} className="flex items-center justify-between text-xs py-1.5 border-b border-slate-800">
              <span className="text-slate-300">${b.amount || 0} · {b.status || "—"}</span>
              <span className="text-slate-500">{b.created_at ? new Date(b.created_at).toLocaleDateString() : ""}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ───────── Push notifications block (in-Profile) ─────────
   Renders inside the "Notifications" tab. Lets the user enable/disable Web Push
   on this device, toggle email on/off, choose categories, and send a test push.
   Talks to /api/notifications/preferences + /api/push/{subscribe,unsubscribe,test}. */
function PushNotificationsBlock({ token, t }) {
  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState(false);
  const [permission, setPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "default");
  const [endpoint, setEndpoint] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/notifications/preferences`, auth(token));
        if (alive) setPrefs(r.data);
        const ep = await getCurrentSubscriptionEndpoint();
        if (alive) setEndpoint(ep);
      } catch { /* noop */ }
    })();
    return () => { alive = false; };
  }, [token]);

  const savePref = async (patch) => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/notifications/preferences`, patch, auth(token));
      setPrefs(r.data);
    } catch { toast.error(t("تعذّر الحفظ", "Save failed")); }
    finally { setBusy(false); }
  };

  const enable = async () => {
    setBusy(true);
    try {
      const r = await ensurePushRegistered(token);
      if (r.ok) {
        setPermission("granted");
        setEndpoint(r.endpoint);
        if (prefs && !prefs.push_enabled) await savePref({ push_enabled: true });
        else toast.success(t("تم تفعيل الإشعارات ✅", "Notifications enabled ✅"));
      } else if (r.reason === "denied") {
        setPermission("denied");
        toast.error(t("تم رفض الإذن من المتصفح. فعّله من إعدادات الموقع.", "Browser permission denied — enable it from site settings."));
      } else {
        toast.error(t("تعذّر التفعيل", "Could not enable"));
      }
    } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await disablePush(token);
      setEndpoint(null);
      await savePref({ push_enabled: false });
      toast.success(t("تم إيقاف الإشعارات على هذا الجهاز.", "Notifications disabled on this device."));
    } finally { setBusy(false); }
  };

  const test = async () => {
    setBusy(true);
    try {
      const r = await sendTestPush(token);
      if (r.delivered > 0) toast.success(t("تم إرسال إشعار تجريبي ✅", "Test push sent ✅"));
      else toast.message(t("لم يصل لأي جهاز — تأكد من التفعيل.", "No devices delivered — ensure it's enabled."));
    } catch { toast.error(t("فشل الإرسال", "Send failed")); }
    finally { setBusy(false); }
  };

  if (!prefs) return <div className="text-xs text-slate-400">{t("جاري التحميل…", "Loading…")}</div>;

  const supported = typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window;
  const subscribed = !!endpoint && permission === "granted" && prefs.push_enabled;

  return (
    <div className="space-y-3 bg-slate-900/40 border border-slate-800 rounded-xl p-4" data-testid="push-block">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-bold text-white">{t("إشعارات الدفع (Push)", "Push notifications")}</div>
          <div className="text-[11px] text-slate-400 mt-0.5">
            {!supported ? t("متصفحك لا يدعم Push", "Your browser does not support Web Push")
              : subscribed ? t("مفعّل على هذا الجهاز", "Enabled on this device")
              : t("غير مفعّل على هذا الجهاز", "Not enabled on this device")}
          </div>
        </div>
        <div className="flex gap-2">
          {subscribed ? (
            <>
              <Button size="sm" variant="outline" onClick={test} disabled={busy} className="border-slate-700 text-slate-200" data-testid="push-test-btn">
                {t("اختبار", "Test")}
              </Button>
              <Button size="sm" variant="outline" onClick={disable} disabled={busy} className="border-red-500/40 text-red-300" data-testid="push-disable-btn">
                {t("إيقاف", "Disable")}
              </Button>
            </>
          ) : (
            <Button size="sm" onClick={enable} disabled={busy || !supported} className="btn-primary" data-testid="push-enable-btn">
              {t("تفعيل", "Enable")}
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between py-1.5 border-t border-slate-800 pt-3">
        <span className="text-sm text-slate-200">{t("إشعارات البريد الإلكتروني", "Email notifications")}</span>
        <Toggle value={!!prefs.email_enabled} onChange={(v) => savePref({ email_enabled: v })} testid="pref-email" />
      </div>

      <div className="pt-2">
        <div className="text-xs text-slate-300 mb-2">{t("الفئات", "Categories")}</div>
        <div className="grid grid-cols-1 gap-1.5">
          {[
            ["support",       t("دعم Arab Sanad", "Arab Sanad Support")],
            ["messages",      t("الرسائل الخاصة", "Direct messages")],
            ["provider",      t("اشتراك مقدمي الخدمات", "Provider subscription")],
            ["jobs",          t("تنبيهات الوظائف", "Job alerts")],
            ["media",         t("اعتماد/رفض الصور والفيديوهات", "Media approval / rejection")],
            ["announcements", t("إعلانات Arab Sanad", "Arab Sanad announcements")],
          ].map(([k, label]) => (
            <div key={k} className="flex items-center justify-between py-1.5 border-b border-slate-800/60">
              <span className="text-[13px] text-slate-200">{label}</span>
              <Toggle
                value={prefs.categories?.[k] !== false}
                onChange={(v) => savePref({ categories: { [k]: v } })}
                testid={`pref-cat-${k}`}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
